"use client";

import { c as _c } from "react/compiler-runtime";
import CircleLoader, { CircleLoaderSize } from "@/components/distribution-plan-tool/common/CircleLoader";
import CurationEmptyState from "@/components/brain/my-stream/curations/CurationEmptyState";
import { Spinner } from "@/components/dotLoader/DotLoader";
import { TweetPreviewModeProvider } from "@/components/tweets/TweetPreviewModeContext";
import CommonIntersectionElement from "@/components/utils/CommonIntersectionElement";
import Drop, { DropLocation } from "@/components/waves/drops/Drop";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useCurationManagementPermission } from "@/hooks/useCurationManagementPermission";
import { useDropCurationMembershipMutation } from "@/hooks/drops/useDropCurationMembershipMutation";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import { useWaveCurationDrops } from "@/hooks/useWaveCurationDrops";
import type { ApiWave } from "@/generated/models/ApiWave";
import { useApprovalWaveStatus } from "@/hooks/waves/useApprovalWaveStatus";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useCallback, useMemo, type ReactNode } from "react";
import { useLayout } from "../layout/LayoutContext";
interface MyStreamWaveCurationContentProps {
  readonly wave: ApiWave;
  readonly curationId: string;
  readonly curationName?: string | null | undefined;
  readonly onDropClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly constrainToViewport?: boolean | undefined;
}
function MyStreamWaveCurationDropItem(t0) {
  const $ = _c(28);
  const {
    drop,
    previousDrop,
    nextDrop,
    curationId,
    canManageActiveCuration,
    onDropClick,
    winningThreshold,
    isVotingClosed,
    isVotingControlsLocked
  } = t0;
  const {
    hasTouchScreen,
    isApp
  } = useDeviceInfo();
  const isTouchDevice = useIsTouchDevice();
  let t1;
  if ($[0] !== drop.id) {
    t1 = {
      dropId: drop.id
    };
    $[0] = drop.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    updateMembership,
    isPending
  } = useDropCurationMembershipMutation(t1);
  const shouldUseDetachedRemoveButton = isTouchDevice || isApp && hasTouchScreen;
  let t2;
  if ($[2] !== curationId || $[3] !== updateMembership) {
    t2 = () => {
      updateMembership(curationId, "remove");
    };
    $[2] = curationId;
    $[3] = updateMembership;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleRemove = t2;
  const t3 = `tw-group tw-relative ${canManageActiveCuration && shouldUseDetachedRemoveButton ? "tw-pt-5" : ""}`;
  let t4;
  if ($[5] !== canManageActiveCuration || $[6] !== handleRemove || $[7] !== isPending || $[8] !== shouldUseDetachedRemoveButton) {
    t4 = canManageActiveCuration && shouldUseDetachedRemoveButton && <button type="button" onClick={event => {
      event.stopPropagation();
      handleRemove();
    }} disabled={isPending} aria-label="Remove drop from this curation" className="tw-absolute tw-right-5 tw-top-0 tw-z-20 tw-inline-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-iron-300 tw-transition-colors tw-duration-200 active:tw-text-iron-100 disabled:tw-cursor-not-allowed disabled:tw-opacity-60">{isPending ? <Spinner dimension={12} /> : <XMarkIcon className="tw-size-4 tw-flex-shrink-0" />}</button>;
    $[5] = canManageActiveCuration;
    $[6] = handleRemove;
    $[7] = isPending;
    $[8] = shouldUseDetachedRemoveButton;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== drop || $[11] !== isVotingClosed || $[12] !== isVotingControlsLocked || $[13] !== nextDrop || $[14] !== onDropClick || $[15] !== previousDrop || $[16] !== winningThreshold) {
    t5 = <Drop key={drop.stableKey} drop={drop} previousDrop={previousDrop} nextDrop={nextDrop} showWaveInfo={false} activeDrop={null} showReplyAndQuote={false} location={DropLocation.WAVE} dropViewDropId={null} onReply={_temp} onReplyClick={_temp2} onQuoteClick={_temp3} onDropContentClick={onDropClick} winningThreshold={winningThreshold} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} />;
    $[10] = drop;
    $[11] = isVotingClosed;
    $[12] = isVotingControlsLocked;
    $[13] = nextDrop;
    $[14] = onDropClick;
    $[15] = previousDrop;
    $[16] = winningThreshold;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== canManageActiveCuration || $[19] !== handleRemove || $[20] !== isPending || $[21] !== shouldUseDetachedRemoveButton) {
    t6 = canManageActiveCuration && !shouldUseDetachedRemoveButton && <button type="button" onClick={event_0 => {
      event_0.stopPropagation();
      handleRemove();
    }} disabled={isPending} aria-label="Remove drop from this curation" title="Remove from wave" className="tw-absolute tw-right-7 tw-top-4 tw-z-20 tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-white/10 tw-bg-black/50 tw-p-0 tw-text-iron-400 tw-shadow-[0_10px_30px_rgba(0,0,0,0.32)] tw-backdrop-blur-sm tw-transition-all tw-duration-200 tw-ease-out hover:tw-border-rose-500/30 hover:tw-bg-rose-500/20 hover:tw-text-rose-300 active:tw-bg-rose-500/15 disabled:tw-cursor-not-allowed disabled:tw-opacity-60 desktop-hover:tw-pointer-events-none desktop-hover:tw-w-auto desktop-hover:tw-translate-y-1 desktop-hover:tw-gap-1.5 desktop-hover:tw-px-2.5 desktop-hover:tw-text-xs desktop-hover:tw-font-medium desktop-hover:tw-opacity-0 desktop-hover:group-hover:tw-pointer-events-auto desktop-hover:group-hover:tw-translate-y-0 desktop-hover:group-hover:tw-opacity-100">{isPending ? <><Spinner dimension={12} /><span className="tw-hidden desktop-hover:tw-inline">Removing</span></> : <><XMarkIcon className="tw-size-4 tw-flex-shrink-0 desktop-hover:tw-size-3.5" /><span className="tw-hidden desktop-hover:tw-inline">Remove</span></>}</button>;
    $[18] = canManageActiveCuration;
    $[19] = handleRemove;
    $[20] = isPending;
    $[21] = shouldUseDetachedRemoveButton;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== t3 || $[24] !== t4 || $[25] !== t5 || $[26] !== t6) {
    t7 = <div className={t3}>{t4}{t5}{t6}</div>;
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  return t7;
}
function _temp3() {}
function _temp2() {}
function _temp() {}
export default function MyStreamWaveCurationContent(t0) {
  const $ = _c(37);
  const {
    wave,
    curationId,
    curationName,
    onDropClick,
    constrainToViewport: t1
  } = t0;
  const constrainToViewport = t1 === undefined ? true : t1;
  const {
    leaderboardViewStyle
  } = useLayout();
  let t2;
  if ($[0] !== curationId || $[1] !== wave) {
    t2 = {
      wave,
      curationId
    };
    $[0] = curationId;
    $[1] = wave;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    drops,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage
  } = useWaveCurationDrops(t2);
  const permissionProbeDropId = drops[0]?.id ?? "";
  let t3;
  if ($[3] !== curationId || $[4] !== permissionProbeDropId) {
    t3 = {
      curationId,
      probeDropId: permissionProbeDropId
    };
    $[3] = curationId;
    $[4] = permissionProbeDropId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const canManageActiveCuration = useCurationManagementPermission(t3);
  const isInitialLoading = isFetching && drops.length === 0;
  let t4;
  if ($[6] !== wave) {
    t4 = {
      wave
    };
    $[6] = wave;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    winningThreshold,
    isVotingClosed,
    isVotingControlsLocked
  } = useApprovalWaveStatus(t4);
  let t5;
  if ($[8] !== fetchNextPage || $[9] !== hasNextPage || $[10] !== isFetchingNextPage) {
    t5 = isIntersecting => {
      if (!isIntersecting || !hasNextPage || isFetchingNextPage) {
        return;
      }
      fetchNextPage();
    };
    $[8] = fetchNextPage;
    $[9] = hasNextPage;
    $[10] = isFetchingNextPage;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleBottomIntersection = t5;
  let t6;
  if ($[12] !== curationName) {
    t6 = curationName?.trim() ?? "Curation";
    $[12] = curationName;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const curationTitle = t6;
  let t7;
  if ($[14] !== canManageActiveCuration || $[15] !== curationId || $[16] !== drops || $[17] !== isVotingClosed || $[18] !== isVotingControlsLocked || $[19] !== onDropClick || $[20] !== winningThreshold) {
    t7 = drops.map((drop, index) => <MyStreamWaveCurationDropItem key={drop.stableKey} drop={drop} previousDrop={index > 0 ? drops[index - 1] ?? null : null} nextDrop={drops[index + 1] ?? null} curationId={curationId} canManageActiveCuration={canManageActiveCuration} onDropClick={onDropClick} winningThreshold={winningThreshold} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} />);
    $[14] = canManageActiveCuration;
    $[15] = curationId;
    $[16] = drops;
    $[17] = isVotingClosed;
    $[18] = isVotingControlsLocked;
    $[19] = onDropClick;
    $[20] = winningThreshold;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const renderedDrops = t7;
  let content;
  if (isInitialLoading) {
    let t8;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center"><CircleLoader size={CircleLoaderSize.XXLARGE} /></div>;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    content = t8;
  } else {
    if (drops.length === 0) {
      const t8 = constrainToViewport ? "tw-flex tw-flex-1 tw-items-center tw-justify-center tw-px-6" : undefined;
      let t9;
      if ($[23] !== curationTitle || $[24] !== t8) {
        t9 = <CurationEmptyState curationTitle={curationTitle} containerClassName={t8} />;
        $[23] = curationTitle;
        $[24] = t8;
        $[25] = t9;
      } else {
        t9 = $[25];
      }
      content = t9;
    } else {
      let t8;
      if ($[26] !== handleBottomIntersection || $[27] !== hasNextPage || $[28] !== isFetchingNextPage) {
        t8 = (hasNextPage || isFetchingNextPage) && <div className="tw-py-4">{isFetchingNextPage ? <div className="tw-flex tw-justify-center"><CircleLoader size={CircleLoaderSize.MEDIUM} /></div> : <CommonIntersectionElement onIntersection={handleBottomIntersection} />}</div>;
        $[26] = handleBottomIntersection;
        $[27] = hasNextPage;
        $[28] = isFetchingNextPage;
        $[29] = t8;
      } else {
        t8 = $[29];
      }
      let t9;
      if ($[30] !== renderedDrops || $[31] !== t8) {
        t9 = <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">{renderedDrops}{t8}</div>;
        $[30] = renderedDrops;
        $[31] = t8;
        $[32] = t9;
      } else {
        t9 = $[32];
      }
      content = t9;
    }
  }
  const t8 = constrainToViewport ? "tw-flex tw-h-full tw-min-h-0 tw-w-full tw-min-w-0 tw-flex-grow tw-flex-col tw-overflow-y-auto tw-overflow-x-hidden tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 desktop-hover:hover:tw-scrollbar-thumb-iron-300" : "tw-flex tw-min-h-0 tw-w-full tw-min-w-0 tw-flex-col";
  const t9 = constrainToViewport ? leaderboardViewStyle : undefined;
  let t10;
  if ($[33] !== content || $[34] !== t8 || $[35] !== t9) {
    t10 = <TweetPreviewModeProvider mode="never"><div className={t8} style={t9}>{content}</div></TweetPreviewModeProvider>;
    $[33] = content;
    $[34] = t8;
    $[35] = t9;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  return t10;
}